import { useState } from 'react';

import { hasSamePicks } from '../utils';

interface Params {
  /** The applied picks, which the filter shows while it is closed. */
  value: readonly string[];
  /** Applies the picks made while the filter was open; called as it closes, only when they differ from `value`. */
  onApply: (value: string[]) => void;
}

/**
 * The picks of a multi-select filter that apply when its menu or drawer closes, so several picks send one request.
 * Picks that end up as the applied ones, in any order, apply nothing. While the filter is open it shows the picks made
 * so far; once closed, the applied ones.
 */
export const usePicksOnClose = ({ value, onApply }: Params) => {
  // The picks made since the filter opened; `null` until the first one
  const [draft, setDraft] = useState<string[] | null>(null);

  const handleOpenChange = (isOpen: boolean) => {
    const isClosingWithChange = !isOpen && draft !== null && !hasSamePicks({ picks: draft, applied: value });

    if (isClosingWithChange) {
      onApply(draft);
    }

    setDraft(null);
  };

  return { picks: draft ?? value, handlePicksChange: setDraft, handleOpenChange };
};
